import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { nodeStorage } from '../services/knowledgeApi'
import { createDirectoryNode, createDocument, NodeType } from '../types'

export const useDirectoryStore = defineStore('knowledgeDirectory', () => {
  const flatNodes = ref([])
  const expandedNodeIds = ref(new Set())
  const selectedNodeId = ref(null)
  const isLoading = ref(false)

  // 构建树形结构
  const treeData = computed(() => {
    return buildTree(null)
  })

  function buildTree(parentId) {
    return flatNodes.value
      .filter(n => n.parent_id === parentId)
      .sort((a, b) => a.order - b.order)
      .map(node => ({
        ...node,
        children: buildTree(node.node_id),
        isExpanded: expandedNodeIds.value.has(node.node_id)
      }))
  }

  async function fetchTree(workspaceId) {
    isLoading.value = true
    try {
      flatNodes.value = await nodeStorage.getByWorkspace(workspaceId)
    } finally {
      isLoading.value = false
    }
  }

  async function createNode(workspaceId, parentId, name, type = NodeType.FOLDER) {
    const siblings = flatNodes.value.filter(n => n.parent_id === parentId)
    const order = siblings.length
    const node = createDirectoryNode(workspaceId, parentId, name, type, order)

    // 如果是文档节点，同时传递文档数据给后端一次性创建
    let doc = null
    if (type === NodeType.DOCUMENT) {
      doc = createDocument(node.node_id, workspaceId, name)
    }
    await nodeStorage.create(node, doc)
    flatNodes.value.push(node)

    return doc ? { node, document: doc } : { node }
  }

  async function renameNode(nodeId, newName) {
    const updated = await nodeStorage.update(nodeId, { name: newName })
    const index = flatNodes.value.findIndex(n => n.node_id === nodeId)
    if (index !== -1) flatNodes.value[index] = updated
  }

  async function deleteNode(nodeId) {
    // 先收集要删除的 ID
    const idsToDelete = new Set()
    const collectChildren = (pid) => {
      idsToDelete.add(pid)
      flatNodes.value.filter(c => c.parent_id === pid).forEach(c => collectChildren(c.node_id))
    }
    collectChildren(nodeId)

    // 获取 workspaceId 用于后端递归删除
    const node = flatNodes.value.find(n => n.node_id === nodeId)
    await nodeStorage.remove(nodeId, node?.workspace_id)
    flatNodes.value = flatNodes.value.filter(n => !idsToDelete.has(n.node_id))
  }

  async function moveNode(nodeId, newParentId, newOrder) {
    const updated = await nodeStorage.move(nodeId, newParentId, newOrder)
    const index = flatNodes.value.findIndex(n => n.node_id === nodeId)
    if (index !== -1) flatNodes.value[index] = updated
  }

  function toggleExpand(nodeId) {
    if (expandedNodeIds.value.has(nodeId)) {
      expandedNodeIds.value.delete(nodeId)
    } else {
      expandedNodeIds.value.add(nodeId)
    }
    // 触发响应式更新
    expandedNodeIds.value = new Set(expandedNodeIds.value)
  }

  function expandToNode(nodeId) {
    // 展开从根到目标节点的路径
    let current = flatNodes.value.find(n => n.node_id === nodeId)
    while (current && current.parent_id) {
      expandedNodeIds.value.add(current.parent_id)
      current = flatNodes.value.find(n => n.node_id === current.parent_id)
    }
    expandedNodeIds.value = new Set(expandedNodeIds.value)
  }

  function selectNode(nodeId) {
    selectedNodeId.value = nodeId
  }

  return {
    flatNodes,
    treeData,
    expandedNodeIds,
    selectedNodeId,
    isLoading,
    fetchTree,
    createNode,
    renameNode,
    deleteNode,
    moveNode,
    toggleExpand,
    expandToNode,
    selectNode
  }
})
